/**
 * Cookie Consent Banner
 *
 * Fixed banner at bottom of screen for first-time visitors.
 * Provides options to Accept All, Reject (Necessary Only), or Customize preferences.
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Cookie } from 'lucide-react'
import { Card, CardContent, Button } from '@tonesmith/ui'
import { useCookieConsentStore } from '@/stores/cookie-consent.store'
import { analytics } from '@/lib/analytics'
import { CookiePreferencesModal } from './CookiePreferencesModal'

export function CookieConsentBanner() {
  const { t } = useTranslation('common')
  const { hasConsented, acceptAll, rejectAll } = useCookieConsentStore()
  const [showPreferences, setShowPreferences] = useState(false)

  // Don't show banner if user has already consented
  if (hasConsented) {
    return null
  }

  const handleAcceptAll = () => {
    acceptAll()
    analytics.onConsentGranted()
  }

  const handleRejectAll = () => {
    rejectAll()
    // Analytics stays disabled (not initialized)
  }

  return (
    <>
      <div className="fixed inset-x-0 bottom-0 z-50 p-4">
        <Card className="mx-auto max-w-4xl border shadow-lg">
          <CardContent className="flex flex-col gap-4 p-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3">
              <Cookie className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
              <div className="space-y-1">
                <p className="font-medium">{t('cookies.banner.title')}</p>
                <p className="text-sm text-muted-foreground">
                  {t('cookies.banner.description')}
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 sm:shrink-0">
              <Button variant="outline" size="sm" onClick={() => setShowPreferences(true)}>
                {t('cookies.banner.customize')}
              </Button>
              <Button variant="outline" size="sm" onClick={handleRejectAll}>
                {t('cookies.banner.rejectAll')}
              </Button>
              <Button size="sm" onClick={handleAcceptAll}>
                {t('cookies.banner.acceptAll')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      <CookiePreferencesModal open={showPreferences} onOpenChange={setShowPreferences} />
    </>
  )
}
